/**
 * ImageDiff Component
 * Displays image changes as a thumbnail grid with add/remove indicators.
 */

import { Plus, X } from 'lucide-react';
import { getProxiedImageUrl } from '@/lib/image-proxy';

interface ImageDiffProps {
  /** URLs of removed images */
  removed: string[];
  /** URLs of added images */
  added: string[];
}

export function ImageDiff({ removed, added }: ImageDiffProps): JSX.Element {
  const hasRemoved = removed.length > 0;
  const hasAdded = added.length > 0;

  if (!hasRemoved && !hasAdded) {
    return (
      <div className="text-sm text-muted-foreground py-2">
        No image changes
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="image-diff">
      {hasRemoved && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Removed ({String(removed.length)})
          </div>
          <div
            className="flex flex-wrap gap-2"
            data-testid="image-diff-removed"
          >
            {removed.map((url, index) => (
              <div
                key={`removed-${String(index)}`}
                className="relative w-16 h-16 rounded-md overflow-hidden border border-red-300 bg-red-50"
              >
                <img
                  src={getProxiedImageUrl(url, 'thumb')}
                  alt={`Removed image ${String(index + 1)}`}
                  className="w-full h-full object-cover opacity-70"
                />
                <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                  <X className="w-3 h-3 text-white" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasAdded && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Added ({String(added.length)})
          </div>
          <div
            className="flex flex-wrap gap-2"
            data-testid="image-diff-added"
          >
            {added.map((url, index) => (
              <div
                key={`added-${String(index)}`}
                className="relative w-16 h-16 rounded-md overflow-hidden border border-green-300 bg-green-50"
              >
                <img
                  src={getProxiedImageUrl(url, 'thumb')}
                  alt={`Added image ${String(index + 1)}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                  <Plus className="w-3 h-3 text-white" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
